import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { User, OvertimeRequest } from "@/types"
import { UserRole, OvertimeStatus, StatIcon } from "@/types/enums"
import { format } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getRoleLabel(role: UserRole): string {
  switch (role) {
    case UserRole.DEPARTMENT_HEAD:
      return 'Department Head';
    case UserRole.PLANT_MANAGER:
      return 'Plant Manager';
    case UserRole.HR:
      return 'HR';
    case UserRole.SUPER_ADMIN:
      return 'Super Admin';
    case UserRole.EMPLOYEE:
      return 'Employee';
    case UserRole.APP_OWNER:
      return 'App Owner';
    default:
      return String(role);
  }
}

// Date and time formatting utilities
export function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  try {
    // Parse date string manually to avoid timezone issues
    // Date-only strings like "2024-10-23" should be displayed as-is without timezone conversion
    const [year, month, day] = dateString.split(/[-T]/);
    if (year && month && day) {
      // Create date in local timezone (not UTC) to prevent day shifts
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(date, 'MMM d, yyyy');
    }
    // Fallback for datetime strings
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

export function formatTime(timeString?: string): string {
  if (!timeString) return 'N/A';
  try {
    // Handle both time strings and datetime strings
    const date = timeString.includes('T') 
      ? new Date(timeString) 
      : new Date(`1970-01-01T${timeString}`);
    
    return format(date, 'h:mm a');
  } catch {
    return timeString;
  }
}

export function formatDatetime(datetimeString?: string): string {
  if (!datetimeString) return 'N/A';
  try {
    return format(new Date(datetimeString), 'MMM d, yyyy h:mm a');
  } catch {
    return datetimeString;
  }
}

export function formatHours(hours?: number): string {
  if (hours === undefined || hours === null) return 'N/A';
  return `${hours} ${hours === 1 ? 'hour' : StatIcon.HOURS}`;
}


// Permission functions
export function canApproveOvertime(user: User): boolean {
  const approvalRoles: UserRole[] = [UserRole.DEPARTMENT_HEAD, UserRole.PLANT_MANAGER, UserRole.HR, UserRole.SUPER_ADMIN, UserRole.APP_OWNER];
  return approvalRoles.includes(user.role as UserRole);
}

export function canEditRequest(user: User, request: OvertimeRequest): boolean {
  // Only request creator or admin can edit
  const isCreator = user.id === request.submittedById;
  const isAdmin = user.role === UserRole.SUPER_ADMIN || user.role === UserRole.APP_OWNER;
  const isPending = request.status === OvertimeStatus.PENDING;
  
  return (isCreator || isAdmin) && isPending;
}

/**
 * Returns ISO string (YYYY-MM-DD) for yesterday's date
 * Useful for date input min attribute to allow selecting yesterday
 * Handles timezone issues by setting time to noon
 */
export function getYesterdayDate(): string {
  const today = new Date();
  // Set to noon to avoid timezone issues
  today.setHours(12, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
}

/**
 * Returns ISO string (YYYY-MM-DD) for the minimum allowed overtime date
 * Useful for date input min attribute to allow selecting up to N days in the past
 * Handles timezone issues by setting time to noon
 */
export function getMinOvertimeDate(): string {
  const today = new Date();
  // Set to noon to avoid timezone issues
  today.setHours(12, 0, 0, 0);
  const minDate = new Date(today);
  minDate.setDate(today.getDate() - 5); // 5 days in the past
  return minDate.toISOString().split('T')[0];
}

/**
 * @deprecated Use getMinOvertimeDate() instead
 * Kept for backward compatibility
 */
export function getLast3DaysDate(): string {
  return getMinOvertimeDate();
}
